/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데스크탑 클래스를 정의한다.
 *
 * @file /src/components/dashboard/Dashboard.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2025. 9. 15.
 */
import * as parent from '@components/panel/Panel';
import { Widget } from '@elements/widget/Widget';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Panel.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 그리드 column 수
     */
    columns: number;

    /**
     * 그리드 row 높이값
     */
    rowHeight?: number;

    /**
     * 위젯 목록
     */
    widgets: Widget['properties'][];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Panel extends parent.Panel {
    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'desktop';

    /**
     * 그리드 column 수
     */
    columns: number = 1;

    /**
     * 그리드 row 높이값
     */
    rowHeight: number | null = null;

    /**
     * 위젯 2차원 배열
     */
    grid: Widget['properties'][][];

    /**
     * 위젯 목록
     */
    widgets: Widget['properties'][];

    /**
     * 생성된 위젯 인스턴스 목록 (id -> Widget)
     */
    widgetInstances: Map<string, Widget> = new Map();

    /**
     * 위젯 목록 Dom
     */
    $widgets: Dom = null;

    /**
     * 데스크탑 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Panel> = null) {
        super(properties);

        this.columns = this.properties.columns;
        this.rowHeight = this.properties.rowHeight ?? 200;
        this.widgets = this.properties.widgets;
        this.scrollable = 'y';
        this.$scrollable = this.$getContent();
    }

    /**
     * 위젯 목록 Dom을 가져온다.
     *
     * @returns 위젯 목록 Dom
     */
    $getWidgets(): Dom {
        if (this.$widgets === null) {
            this.$widgets = Html.create('div', { 'data-role': 'widgets' });
        }

        return this.$widgets;
    }

    /**
     * 순서를 유지하며 그리드에 맞게 위젯을 배치
     */
    private arrangeWidgetsInGrid(): void {
        const rows: Widget['properties'][][] = [];
        const widgetsCopy = [...this.widgets];

        while (widgetsCopy.length > 0) {
            const row = this.fillRow(widgetsCopy);
            if (row.length === 0) break; // 무한 루프 방지
            rows.push(row);
        }

        this.grid = rows;
        this.widgets = rows.flat();
    }

    /**
     * 한 행을 채울 수 있는 위젯들을 찾아서 배치
     *
     * @param widgets - 위젯 목록
     * @returns 한 행에 채울 수 있는 위젯 목록
     */
    private fillRow(widgets: Widget['properties'][]): Widget['properties'][] {
        let remainingColumns = this.columns;
        const row: Widget['properties'][] = [];

        while (remainingColumns > 0 && widgets.length > 0) {
            const widgetIndex = widgets.findIndex((widget) => (widget.column || 1) <= remainingColumns);
            if (widgetIndex === -1) break;

            const [pickedWidget] = widgets.splice(widgetIndex, 1);
            row.push(pickedWidget);
            remainingColumns -= pickedWidget.column || 1;
        }

        return row;
    }

    /**
     * 위젯 DOM 순서를 재정렬한다
     */
    reorderWidgets(): void {
        this.arrangeWidgetsInGrid();
        this.updateDomOrder();
    }

    /**
     * DOM 순서를 새로운 위젯 순서에 맞게 업데이트
     */
    private updateDomOrder(): void {
        for (const widget of this.widgets) {
            const instance = this.widgetInstances.get(widget.id);
            if (instance) {
                this.$getWidgets().append(instance.$getElement());
            }
        }
    }

    /**
     * 개별 위젯을 생성하고 렌더링
     *
     * @param properties - 위젯 프로퍼티
     */
    private async renderWidget(properties: Widget['properties']): Promise<void> {
        const widget = new Widget(properties);
        widget.setComponent(this);
        this.widgetInstances.set(widget.id, widget);

        this.$getWidgets().append(widget.$getElement());
        await widget.render();
    }

    /**
     * 위젯 목록을 렌더링한다
     */
    async renderWidgets(): Promise<void> {
        this.arrangeWidgetsInGrid();

        for (const widget of this.widgets) {
            await this.renderWidget(widget);
        }

        this.$getContent().append(this.$getWidgets());
    }

    /**
     * 컴포넌트 DOM을 렌더링한다
     */
    async renderDom(): Promise<void> {
        if (this.$getContainer().isConnected() == false) {
            this.$getComponent().append(this.$getContainer());
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        this.$getContainer().append(this.$getContent());

        await this.renderWidgets();
    }

    /**
     * 컴포넌트 상태를 렌더링한다
     */
    async renderState(): Promise<void> {
        await super.renderState();

        const contentEl = this.$getContent().getEl();
        contentEl.style.setProperty('--aui-desktop-columns', this.columns.toString());
        contentEl.style.setProperty('--aui-desktop-rowHeight', `${this.rowHeight}px`);
    }
}
